import React, { forwardRef, ReactNode, useEffect } from "react";
import { View, Platform } from "react-native";
import getGlobalStyles from "../globalStyles";
import {
  Gesture,
  GestureDetector,
  GestureHandlerRootView,
  GestureStateChangeEvent,
} from "react-native-gesture-handler";
import { PanGestureHandlerEventPayload } from "react-native-screens";
import {
  runOnJS,
  useSharedValue,
} from "react-native-reanimated";

interface VerticalGestureHandlerProps {
  children: ReactNode;
  swipeUp?: (
    event?: GestureStateChangeEvent<PanGestureHandlerEventPayload>
  ) => void;
  swipeDown?: (
    event?: GestureStateChangeEvent<PanGestureHandlerEventPayload>
  ) => void;
  disabled?: boolean;
}

/* ---------------- Production-grade tuning ---------------- */
const HORIZONTAL_FAIL_RANGE = [-12, 12]; // horizontal fails instantly
const SWIPE_DISTANCE = 70;               // minimum distance for intentional swipe
const SWIPE_VELOCITY = 400;              // minimum velocity
const VERTICAL_DOMINANCE_RATIO = 1.5;    // vertical movement must dominate horizontal

const VerticalGestureHandler = forwardRef<View, VerticalGestureHandlerProps>(
  ({ children, swipeUp, swipeDown, disabled }, ref) => {
    const triggered = useSharedValue(false);
    const gStyles = getGlobalStyles();

    useEffect(() => {
      if (Platform.OS !== "web") return;

      let startY = 0, startX = 0, startTime = 0;

      const onTouchStart = (e: TouchEvent) => {
        startY = e.touches[0].clientY;
        startX = e.touches[0].clientX;
        startTime = Date.now();
      };

      const onTouchEnd = (e: TouchEvent) => {
        if (disabled) return;
        const touch = e.changedTouches[0];
        const dy = touch.clientY - startY;
        const dx = touch.clientX - startX;
        const dt = Math.max(Date.now() - startTime, 1) / 1000;

        if (Math.abs(dy) < SWIPE_DISTANCE) return;
        if (Math.abs(dy) / (Math.abs(dx) + 1) < VERTICAL_DOMINANCE_RATIO) return;
        if (Math.abs(dy) / dt < SWIPE_VELOCITY) return;

        if (dy > 0 && swipeDown) swipeDown();
        else if (dy < 0 && swipeUp) swipeUp();
      };

      window.addEventListener("touchstart", onTouchStart, { passive: true });
      window.addEventListener("touchend", onTouchEnd, { passive: true });
      return () => {
        window.removeEventListener("touchstart", onTouchStart);
        window.removeEventListener("touchend", onTouchEnd);
      };
    }, [disabled, swipeUp, swipeDown]);

    const pan = Gesture.Pan()
      .enabled(!disabled)
      .minDistance(12)
      .failOffsetX(HORIZONTAL_FAIL_RANGE)

      .onEnd((event) => {
        "worklet";
        if (triggered.value) return;

        const { translationY, translationX, velocityY } = event;

        const verticalIntent =
          Math.abs(translationY) > SWIPE_DISTANCE &&
          Math.abs(translationY) / (Math.abs(translationX) + 1) >= VERTICAL_DOMINANCE_RATIO &&
          Math.abs(velocityY) > SWIPE_VELOCITY;

        if (!verticalIntent) return;

        triggered.value = true;

        if (translationY > 0 && swipeDown) {
          runOnJS(swipeDown)(event);
        } else if (translationY <= 0 && swipeUp) {
          runOnJS(swipeUp)(event);
        }

        // reset
        triggered.value = false;
      });

    return (
      <GestureHandlerRootView style={{ flex: 1 }}>
        <GestureDetector gesture={pan}>
          <View
            ref={ref}
            style={[gStyles.centerContainer, { justifyContent: "flex-start" }]}
          >
            {children}
          </View>
        </GestureDetector>
      </GestureHandlerRootView>
    );
  }
);

export default VerticalGestureHandler;
